/**
 * Log Service
 *
 * Unified read + error-logging service for the `logs` collection.
 * Supports three log types: audit, error, ai-usage.
 *
 * Collection: 'logs'
 * - Audit logs: written server-side by onDocumentWritten triggers (functions/src/audit/)
 * - Error logs: written via logErrorEvent Cloud Function
 * - AI-usage logs: written via Admin SDK
 *
 * Client has read-only access (admin/employee).
 */

import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
  type QueryConstraint,
  type Unsubscribe,
  type Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { createLogger } from '@shared/lib';

const log = createLogger('LogService');

const COLLECTION = 'logs';

// ─── Types ───────────────────────────────────────────

export type LogType = 'audit' | 'error' | 'ai-usage';

export type AuditEntityType =
  | 'pipeline-entry'
  | 'email-template'
  | 'email-template-category'
  | 'shared-mailbox'
  | 'signature-template'
  | 'tab-visibility-rule'
  | 'mailbox-rule'
  | 'task'
  | 'task-view'
  | 'career-view'
  | 'snippet'
  | 'snippet-category'
  | 'job-posting'
  | 'settings-auto-reply'
  | 'settings-ai-careers'
  | 'settings-careers'
  | 'feedback';

export type AuditAction =
  | 'create'
  | 'update'
  | 'soft_delete'
  | 'restore'
  | 'hard_delete'
  | 'activate'
  | 'deactivate'
  | 'cancel'
  | 'no_show';

export interface AuditLogEntry {
  id: string;
  type: 'audit';
  action: AuditAction;
  entityType: AuditEntityType;
  entityId: string;
  entityName: string;
  performedBy: string;
  performedAt: Timestamp;
  metadata?: Record<string, unknown>;
}

export interface ErrorLogEntry {
  id: string;
  type: 'error';
  module: string;
  message: string;
  error: string;
  stack?: string;
  url?: string;
  performedBy: string;
  performedAt: Timestamp;
  metadata?: Record<string, unknown>;
}

export interface AiUsageLogEntry {
  id: string;
  type: 'ai-usage';
  module: string;
  message: string;
  performedBy: string;
  performedAt: Timestamp;
  metadata: {
    model: string;
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    userName: string;
    [key: string]: unknown;
  };
}

export type LogEntry = AuditLogEntry | ErrorLogEntry | AiUsageLogEntry;

// ─── Create Data Types ───────────────────────────────

type CreateErrorLogData = Omit<ErrorLogEntry, 'id' | 'performedAt' | 'performedBy' | 'type'> & {
  metadata?: Record<string, unknown>;
};

// ─── Helpers ─────────────────────────────────────────

function transformDoc(id: string, data: Record<string, unknown>): LogEntry {
  const type = data.type as LogType;
  const base = {
    id,
    type,
    performedBy: data.performedBy as string,
    performedAt: data.performedAt as Timestamp,
  };

  if (type === 'audit') {
    return {
      ...base,
      type: 'audit',
      action: data.action as AuditAction,
      entityType: data.entityType as AuditEntityType,
      entityId: data.entityId as string,
      entityName: data.entityName as string,
      ...(data.metadata !== undefined
        ? { metadata: data.metadata as Record<string, unknown> }
        : {}),
    };
  }

  if (type === 'error') {
    return {
      ...base,
      type: 'error',
      module: data.module as string,
      message: data.message as string,
      error: data.error as string,
      ...(data.stack !== undefined ? { stack: data.stack as string } : {}),
      ...(data.url !== undefined ? { url: data.url as string } : {}),
      ...(data.metadata !== undefined
        ? { metadata: data.metadata as Record<string, unknown> }
        : {}),
    };
  }

  // ai-usage
  return {
    ...base,
    type: 'ai-usage',
    module: data.module as string,
    message: data.message as string,
    metadata: data.metadata as AiUsageLogEntry['metadata'],
  };
}

// ─── Service ─────────────────────────────────────────

export const LogService = {
  /**
   * Log a frontend error via Cloud Function.
   * Rate-limiting should be handled by the caller (createLogger).
   */
  async logError(data: CreateErrorLogData): Promise<void> {
    const logFn = httpsCallable<CreateErrorLogData, { success: boolean }>(
      functions,
      'logErrorEvent'
    );
    await logFn(data);
  },

  /**
   * Real-time subscription for log entries
   */
  subscribe(
    callback: (entries: LogEntry[]) => void,
    onError?: (error: Error) => void,
    constraints: QueryConstraint[] = []
  ): Unsubscribe {
    const colRef = collection(db, COLLECTION);
    const allConstraints = [...constraints, orderBy('performedAt', 'desc'), limit(200)];
    const q = query(colRef, ...allConstraints);

    return onSnapshot(
      q,
      (snapshot) => {
        const entries = snapshot.docs.map((doc) => transformDoc(doc.id, doc.data()));
        callback(entries);
      },
      (error) => {
        log.error('Subscription error:', error);
        onError?.(error);
      }
    );
  },

  /**
   * Fetch log entries (one-time)
   */
  async getAll(constraints: QueryConstraint[] = []): Promise<LogEntry[]> {
    const colRef = collection(db, COLLECTION);
    const allConstraints = [...constraints, orderBy('performedAt', 'desc'), limit(200)];
    const q = query(colRef, ...allConstraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => transformDoc(doc.id, doc.data()));
  },
};
